import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Spinner } from "../components/ui/Primitives";
import BrandMark from "../components/BrandMark";
import LanguageToggle from "../components/LanguageToggle";
import LegalFooter from "../components/LegalFooter";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useAuth } from "../contexts/AuthContext";

// Phase 27 — Public pricing page reachable without login.
// Used by:
//   - /login → "Jetzt starten" button
//   - food.studioops.io/pricing
//
// Flow: user picks plan + interval → POST /api/billing/signup-checkout → Stripe Checkout
//   → after payment, redirected to /welcome which auto-creates the account.

const MANDATORY_SETUP = { hotel: { key: "hotel", price: 699, name: "Setup Hotel" },
                           premium: { key: "premium", price: 1499, name: "Setup Premium" } };
const OPTIONAL_SETUP = { key: "basic", price: 399, name: "Setup Basic" };

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState("monthly");
  const [busyPlan, setBusyPlan] = useState(null);
  const [addBasicSetup, setAddBasicSetup] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans((data.plans || []).filter((p) => !p.contact_only));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startCheckout = async (planId) => {
    setBusyPlan(planId);
    try {
      const isMandatory = MANDATORY_SETUP[planId];
      const setup_package = isMandatory
        ? isMandatory.key
        : (addBasicSetup ? OPTIONAL_SETUP.key : null);
      const { data } = await api.post("/billing/signup-checkout", {
        plan: planId,
        interval,
        origin_url: window.location.origin,
        setup_package,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyPlan(null);
    }
  };

  const autoLoginDemo = async (email, password) => {
    try {
      await login(email, password);
      navigate("/dashboard");
      toast.success(t("billing.demo_loaded", "Demo geladen — willkommen!"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bg-void"><Spinner size={28} /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-bg-void" data-testid="pricing-page">
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3" data-testid="brand-to-login">
            <BrandMark />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-ink-secondary hover:text-ink-primary" data-testid="link-back-login">
              {t("auth.have_account", "Bereits Konto? Login")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-wider text-brand font-medium mb-2">
            {t("billing.pricing_eyebrow", "Plan wählen — kein externer Landing nötig")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-ink-primary">
            {t("billing.pricing_headline", "Wählen Sie Ihren Plan")}
          </h1>
          <p className="text-sm text-ink-secondary mt-2">
            {t("billing.pricing_sub", "Direkt nach der Zahlung wird Ihr Konto automatisch angelegt — kein Anmeldeformular nötig.")}
          </p>
        </div>

        {/* Interval toggle */}
        <div className="flex items-center justify-center mb-8" data-testid="pricing-interval-toggle">
          <div className="inline-flex bg-bg-surface border border-white/10 rounded-full p-1">
            <button
              data-testid="interval-monthly"
              onClick={() => setInterval("monthly")}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${interval === "monthly" ? "bg-brand text-white" : "text-ink-secondary hover:text-ink-primary"}`}
            >
              {t("billing.monthly", "Monatlich")}
            </button>
            <button
              data-testid="interval-annual"
              onClick={() => setInterval("annual")}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${interval === "annual" ? "bg-brand text-white" : "text-ink-secondary hover:text-ink-primary"}`}
            >
              {t("billing.annual", "Jährlich")} <span className="text-[10px] text-ok ml-1">-15%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => {
            const price = interval === "annual" ? p.annual : p.monthly;
            const mandatory = MANDATORY_SETUP[p.id];
            return (
              <Card key={p.id} className="p-5 flex flex-col" data-testid={`pricing-card-${p.id}`}>
                <div className="text-base font-semibold text-ink-primary">{p.label}</div>
                <div className="flex items-baseline gap-1.5 my-3">
                  <span className="num text-3xl font-semibold tabular-nums text-ink-primary">
                    € {price.toFixed(0)}
                  </span>
                  <span className="text-xs text-ink-muted">/ {interval === "annual" ? t("billing.year", "Jahr") : t("billing.month", "Monat")}</span>
                </div>
                <ul className="space-y-1.5 text-sm text-ink-secondary mt-1 mb-3 flex-1">
                  <li className="flex items-start gap-2"><Check size={14} className="text-ok mt-0.5 shrink-0" /><span>{p.monthly_credits} {t("billing.credits_per_month")}</span></li>
                  {p.modules.map((m) => (
                    <li key={m} className="flex items-start gap-2"><Check size={14} className="text-ok mt-0.5 shrink-0" /><span>{t(`billing.module.${m}`, m)}</span></li>
                  ))}
                  {mandatory && (
                    <li className="flex items-start gap-2 mt-2 pt-2 border-t border-white/5" data-testid={`pricing-mandatory-${p.id}`}>
                      <AlertTriangle size={14} className="text-warn mt-0.5 shrink-0" />
                      <span>
                        <span className="font-medium text-ink-primary">{mandatory.name} · € {mandatory.price}</span>
                        <span className="block text-[10px] text-warn">{t("billing.mandatory_badge", "Pflichtpaket — im Checkout enthalten")}</span>
                      </span>
                    </li>
                  )}
                </ul>
                <Button onClick={() => startCheckout(p.id)} disabled={busyPlan === p.id} className="w-full" data-testid={`pricing-start-${p.id}`}>
                  {busyPlan === p.id ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {t("billing.continue", "Weiter zu Stripe")}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Optional setup add-on */}
        <Card className="mt-6 p-4 flex items-start gap-3" data-testid="pricing-optional-setup">
          <input
            type="checkbox"
            id="addBasicSetup"
            checked={addBasicSetup}
            onChange={(e) => setAddBasicSetup(e.target.checked)}
            className="mt-1"
            data-testid="pricing-add-basic"
          />
          <label htmlFor="addBasicSetup" className="text-sm text-ink-secondary cursor-pointer">
            <span className="font-medium text-ink-primary">Setup Basic · € 399</span>
            <span className="block text-[11px] text-ink-muted">{t("billing.basic_optional", "Optional: 1-Tages-Onboarding (Empfohlen für Küche/Bar/Pro-Pläne)")}</span>
          </label>
        </Card>

        {/* F13 — Live demo section */}
        <section className="mt-10" data-testid="pricing-live-demo">
          <div className="rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 to-transparent p-6 md:p-8">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="text-brand mt-1" size={20} />
              <div>
                <h2 className="text-xl font-semibold text-ink-primary">{t("billing.demo_title", "Testen Sie StudioOps Food live")}</h2>
                <p className="text-sm text-ink-secondary mt-1">{t("billing.demo_sub", "Kostenlos und ohne Anmeldung — Demo-Zugangsdaten werden automatisch eingetragen.")}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => autoLoginDemo("demo@studioopsfood.at", "Demo2026!")}
                className="text-left rounded-xl border border-white/10 bg-bg-surface px-4 py-4 hover:border-brand/40 transition-colors group"
                data-testid="demo-restaurant-bar"
              >
                <div className="text-[10px] uppercase tracking-wider text-brand font-medium mb-1">Hotel-Restaurant</div>
                <div className="text-base font-semibold text-ink-primary">Demo Restaurant & Bar</div>
                <div className="text-xs text-ink-secondary mt-1">Schloss Fuschl · 15 Rezepte · 10 Cocktails · 3 Zimmer</div>
                <div className="text-[11px] text-brand mt-2 group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                  Tour starten <ArrowRight size={11} />
                </div>
              </button>
              <button
                type="button"
                onClick={() => autoLoginDemo("demo-chain@studioopsfood.at", "Demo2026!")}
                className="text-left rounded-xl border border-white/10 bg-bg-surface px-4 py-4 hover:border-brand/40 transition-colors group"
                data-testid="demo-chain-multi"
              >
                <div className="text-[10px] uppercase tracking-wider text-brand font-medium mb-1">Multi-Outlet</div>
                <div className="text-base font-semibold text-ink-primary">Demo Hotelkette</div>
                <div className="text-xs text-ink-secondary mt-1">3 Outlets · Restaurant + Bar + Hotel · Premium Plan</div>
                <div className="text-[11px] text-brand mt-2 group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                  Tour starten <ArrowRight size={11} />
                </div>
              </button>
            </div>
          </div>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
